var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, CircularProgress, Chip, } from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, People as PeopleIcon, } from '@mui/icons-material';
const RoleManagement = () => {
    const [roles, setRoles] = useState([
        { id: 1, name: 'User', description: 'Standard user with basic access', createdAt: new Date().toISOString(), userCount: 0 },
        { id: 2, name: 'Admin', description: 'Administrator with full system access', createdAt: new Date().toISOString(), userCount: 0 },
        { id: 3, name: 'Owner', description: 'Business owner with all permissions', createdAt: new Date().toISOString(), userCount: 0 },
    ]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
    });
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [alert, setAlert] = useState({
        show: false,
        message: '',
        severity: 'info',
    });
    const showAlert = (message, severity) => {
        setAlert({ show: true, message, severity });
        setTimeout(() => setAlert({ show: false, message: '', severity: 'info' }), 6000);
    };
    const handleOpenDialog = (role) => {
        if (role) {
            setSelectedRole(role);
            setFormData({
                name: role.name,
                description: role.description || '',
            });
        }
        else {
            setSelectedRole(null);
            setFormData({
                name: '',
                description: '',
            });
        }
        setFormErrors({});
        setOpenDialog(true);
    };
    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedRole(null);
        setFormData({ name: '', description: '' });
        setFormErrors({});
    };
    const validateForm = () => {
        const errors = {};
        if (!formData.name.trim()) {
            errors.name = 'Role name is required';
        }
        else if (formData.name.length < 2) {
            errors.name = 'Role name must be at least 2 characters';
        }
        if (!formData.description.trim()) {
            errors.description = 'Description is required';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };
    const handleSubmit = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!validateForm())
            return;
        setSubmitting(true);
        try {
            // Simulate API call
            yield new Promise(resolve => setTimeout(resolve, 500));
            if (selectedRole) {
                // Update existing role
                setRoles(roles.map(r => r.id === selectedRole.id
                    ? Object.assign(Object.assign({}, r), { name: formData.name, description: formData.description }) : r));
                showAlert('Role updated successfully', 'success');
            }
            else {
                // Create new role
                const newRole = {
                    id: Math.max(...roles.map(r => r.id), 0) + 1,
                    name: formData.name,
                    description: formData.description,
                    createdAt: new Date().toISOString(),
                    userCount: 0,
                };
                setRoles([...roles, newRole]);
                showAlert('Role created successfully', 'success');
            }
            handleCloseDialog();
        }
        catch (error) {
            showAlert('Error saving role', 'error');
        }
        finally {
            setSubmitting(false);
        }
    });
    const handleDeleteClick = (role) => {
        setSelectedRole(role);
        setDeleteDialogOpen(true);
    };
    const handleDeleteConfirm = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!selectedRole)
            return;
        // Check if role is a system role
        if (['User', 'Admin', 'Owner'].includes(selectedRole.name)) {
            showAlert('Cannot delete system roles (User, Admin, Owner)', 'error');
            setDeleteDialogOpen(false);
            return;
        }
        setSubmitting(true);
        try {
            // Simulate API call
            yield new Promise(resolve => setTimeout(resolve, 500));
            setRoles(roles.filter(r => r.id !== selectedRole.id));
            showAlert('Role deleted successfully', 'success');
            setDeleteDialogOpen(false);
            setSelectedRole(null);
        }
        catch (error) {
            showAlert('Error deleting role', 'error');
        }
        finally {
            setSubmitting(false);
        }
    });
    const isSystemRole = (roleName) => {
        return ['User', 'Admin', 'Owner'].includes(roleName);
    };
    const columns = [
        {
            field: 'name',
            headerName: 'Role Name',
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(PeopleIcon, { fontSize: "small", color: "primary" }), _jsx(Typography, { variant: "body2", fontWeight: isSystemRole(params.value) ? 'bold' : 'normal', children: params.value }), isSystemRole(params.value) && (_jsx(Chip, { label: "System", size: "small", color: "primary", variant: "outlined" }))] })),
        },
        {
            field: 'description',
            headerName: 'Description',
            flex: 2,
            minWidth: 250,
        },
        {
            field: 'userCount',
            headerName: 'Users',
            width: 100,
            renderCell: (params) => (_jsx(Chip, { label: params.value || 0, size: "small" })),
        },
        {
            field: 'createdAt',
            headerName: 'Created',
            width: 130,
            valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : '',
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            getActions: (params) => [
                _jsx(GridActionsCellItem, { icon: _jsx(EditIcon, {}), label: "Edit", onClick: () => handleOpenDialog(params.row) }),
                _jsx(GridActionsCellItem, { icon: _jsx(DeleteIcon, {}), label: "Delete", onClick: () => handleDeleteClick(params.row), disabled: isSystemRole(params.row.name), showInMenu: true }),
            ],
        },
    ];
    return (_jsxs(Box, { children: [_jsxs(Box, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }, children: [_jsx(Typography, { variant: "h6", children: "Role Management" }), _jsx(Button, { variant: "contained", startIcon: _jsx(AddIcon, {}), onClick: () => handleOpenDialog(), children: "Add Role" })] }), alert.show && (_jsx(Alert, { severity: alert.severity, sx: { mb: 2 }, onClose: () => setAlert(Object.assign(Object.assign({}, alert), { show: false })), children: alert.message })), _jsx(Alert, { severity: "info", sx: { mb: 2 }, children: "Manage system roles. System roles (User, Admin, Owner) cannot be deleted." }), loading ? (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', py: 4 }, children: _jsx(CircularProgress, {}) })) : (_jsx(Box, { sx: { height: 400, width: '100%' }, children: _jsx(DataGrid, { rows: roles, columns: columns, pageSizeOptions: [10, 25, 50], initialState: {
                        pagination: {
                            paginationModel: { pageSize: 10 },
                        },
                    }, disableRowSelectionOnClick: true }) })), _jsxs(Dialog, { open: openDialog, onClose: handleCloseDialog, maxWidth: "sm", fullWidth: true, children: [_jsx(DialogTitle, { children: selectedRole ? 'Edit Role' : 'Create New Role' }), _jsx(DialogContent, { children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }, children: [_jsx(TextField, { label: "Role Name", value: formData.name, onChange: (e) => setFormData(Object.assign(Object.assign({}, formData), { name: e.target.value })), error: !!formErrors.name, helperText: formErrors.name, fullWidth: true, required: true, disabled: selectedRole ? isSystemRole(selectedRole.name) : false }), _jsx(TextField, { label: "Description", value: formData.description, onChange: (e) => setFormData(Object.assign(Object.assign({}, formData), { description: e.target.value })), error: !!formErrors.description, helperText: formErrors.description, fullWidth: true, required: true, multiline: true, rows: 3 }), selectedRole && isSystemRole(selectedRole.name) && (_jsx(Alert, { severity: "warning", children: "System role names cannot be changed. You can only modify the description." }))] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: handleCloseDialog, disabled: submitting, children: "Cancel" }), _jsx(Button, { onClick: handleSubmit, variant: "contained", disabled: submitting, children: submitting ? 'Saving...' : selectedRole ? 'Update' : 'Create' })] })] }), _jsxs(Dialog, { open: deleteDialogOpen, onClose: () => setDeleteDialogOpen(false), children: [_jsx(DialogTitle, { children: "Confirm Delete" }), _jsx(DialogContent, { children: _jsxs(Alert, { severity: "warning", sx: { mb: 2 }, children: ["Are you sure you want to delete the role ", _jsx("strong", { children: selectedRole === null || selectedRole === void 0 ? void 0 : selectedRole.name }), "?", (selectedRole === null || selectedRole === void 0 ? void 0 : selectedRole.userCount) && selectedRole.userCount > 0 && (_jsxs(Box, { component: "span", sx: { display: 'block', mt: 1 }, children: ["This role is assigned to ", selectedRole.userCount, " user(s). They will lose this role."] }))] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: () => setDeleteDialogOpen(false), disabled: submitting, children: "Cancel" }), _jsx(Button, { onClick: handleDeleteConfirm, color: "error", variant: "contained", disabled: submitting, children: submitting ? 'Deleting...' : 'Delete' })] })] })] }));
};
export default RoleManagement;
